import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import Polyline from '@arcgis/core/geometry/Polyline.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import type Point from '@arcgis/core/geometry/Point.js';
import { commitMeasurement } from '@/map/measurements';
import {
  registerHandle,
  resolve,
  summarizeRef,
  type ResolvedRef,
  type Source,
  SourceSchema,
} from '@/agent/refs';
import { linearToEsri, type LinearUnit } from './shared/units';
import type { Tool } from './types';

type Input = { from: Source; to: Source; units?: LinearUnit };

function representativePoint(geom: Geometry): Point | null {
  if (geom.type === 'point') return geom as Point;
  const centroid = (geom as { centroid?: Point | null }).centroid;
  if (centroid) return centroid;
  const ext = (geom as { extent?: __esri.Extent | null }).extent;
  return ext?.center ?? null;
}

function nearestPointOn(geom: Geometry, point: Point): Point {
  if (geom.type === 'point') return geom as Point;
  const result = geometryEngine.nearestCoordinate(
    geom as Parameters<typeof geometryEngine.nearestCoordinate>[0],
    point,
  );
  return (result?.coordinate as Point | undefined) ?? point;
}

export const drawDistance: Tool<Input> = {
  name: 'draw_distance',
  description:
    'Draw a labelled distance line between two Sources on the map and add it to the Measurements panel. The line lands on the nearest edges of each side so it visualises the same edge-to-edge distance compute_distance returns. Use this — not sketch/measure — when the agent already knows both endpoints. Returns the distance plus a handle to the polyline.',
  input_schema: {
    type: 'object',
    properties: {
      from: SourceSchema,
      to: SourceSchema,
      units: { enum: ['meters', 'kilometers', 'feet', 'miles'] },
    },
    required: ['from', 'to'],
    additionalProperties: false,
  },
  execute: async ({ from, to, units = 'meters' }) => {
    const [a, b] = await Promise.all([resolve(from), resolve(to)]);
    if (!a.geometry || !b.geometry) {
      throw new Error('draw_distance: both sources need geometry');
    }

    const aRep = representativePoint(a.geometry);
    const bRep = representativePoint(b.geometry);
    if (!aRep || !bRep) {
      throw new Error('draw_distance: could not derive representative points');
    }

    let aPt =
      a.geometry.type === 'point'
        ? (a.geometry as Point)
        : nearestPointOn(a.geometry, bRep);
    let bPt = nearestPointOn(b.geometry, aPt);
    if (a.geometry.type !== 'point') {
      aPt = nearestPointOn(a.geometry, bPt);
    }

    const sr = aPt.spatialReference ?? bPt.spatialReference ?? undefined;
    const polyline = new Polyline({
      paths: [
        [
          [aPt.x, aPt.y],
          [bPt.x, bPt.y],
        ],
      ],
      spatialReference: sr,
    });

    const valueMeters = geometryEngine.distance(
      a.geometry as Parameters<typeof geometryEngine.distance>[0],
      b.geometry as Parameters<typeof geometryEngine.distance>[1],
      'meters',
    );
    const value = geometryEngine.distance(
      a.geometry as Parameters<typeof geometryEngine.distance>[0],
      b.geometry as Parameters<typeof geometryEngine.distance>[1],
      linearToEsri(units),
    );

    commitMeasurement({ mode: 'distance', geometry: polyline, valueMeters });

    const ref: ResolvedRef = {
      source_kind: 'geometry',
      geometry: polyline,
      extent: polyline.extent ?? undefined,
    };
    const handle = registerHandle(ref);

    return {
      distance: value,
      units,
      handle,
      from_point: { x: aPt.x, y: aPt.y, wkid: sr?.wkid ?? undefined },
      to_point: { x: bPt.x, y: bPt.y, wkid: sr?.wkid ?? undefined },
      summary: summarizeRef(ref),
    };
  },
};
